import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, type DropdownProps } from 'primereact/dropdown';
import { twMerge } from 'tailwind-merge';
import { LiliDropDownItemTemplate } from './LiliDropDownItemTemplate';
import { LiliDropDownValueTemplate } from './LiliDropDownValueTemplate';
import { ReactComponent as ArrowIcon } from '../../../assets/shared/ic-shared-down-arrow.svg';
import { LiliError } from '../LiliError';

// See https://primereact.org/dropdown/ and add any additional props that you need
export interface LiliDropdownItem {
  title: string;
  value: any;
}
export enum LiliDropdownSize {
  Medium,
  Small,
}
export interface LiliDropdownProps extends Omit<DropdownProps, 'onSelect'> {
  items: LiliDropdownItem[];
  title: string;
  value?: LiliDropdownItem;
  onSelect: (item: LiliDropdownItem) => void;
  onClear?: () => void;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  loading?: boolean;
  errorMessage?: string;
  inlineChild?: React.ReactNode;
  /** Used when the dropdown item value is an object. optionValue is a object key name */
  optionValue?: string;
  id?: string;
  showClear?: boolean;
  tabIndex?: number;
  size?: LiliDropdownSize;
  Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  shouldScrollOnOpen?: boolean;
}

export const LiliDropdown = (props: LiliDropdownProps) => {
  const {
    items,
    title,
    value,
    onSelect,
    onClear,
    onBlur,
    onFocus,
    disabled,
    loading,
    errorMessage,
    inlineChild,
    optionValue,
    id,
    showClear,
    tabIndex,
    size = LiliDropdownSize.Medium,
    Icon,
    shouldScrollOnOpen,
    ...otherProps
  } = props;

  const [isOptionPresented, setIsOptionPresented] = useState<boolean>(false);
  const elDropDownWrapper = useRef<HTMLDivElement | null>(null);

  const isItemSelected = (item: LiliDropdownItem) => {
    if (!value) return false;
    if (typeof value === 'object' && optionValue) {
      return value.value?.[optionValue] === item.value?.[optionValue] || false;
    }
    return value.value === item.value || false;
  };

  const dropdownStyle = twMerge(
    `dropdown outline-none shadow-none flex flex-row justify-between items-center pl-[16px] pr-[12px] rounded-[8px] hover:bg-surface-primary-bw-01 w-full border-[1px] border-outline-primary-bw-04`,
    value?.value
      ? `filled ${size === LiliDropdownSize.Medium ? 'border-outline-primary-bw-04' : 'border-outline-primary-bw-04'}`
      : '',
    isOptionPresented && 'border-outline-brand-primary',
    errorMessage && '!border-outline-negative-primary',
    disabled
      ? 'bg-surface-primary-bw-02 cursor-auto hover:bg-surface-primary-bw-02 border-none'
      : 'cursor-pointer',
    size === LiliDropdownSize.Medium ? 'min-h-[54px]' : 'h-[40px] pl-[12px]'
  );

  const dropdownContainerStyle = twMerge(
    `card flex flex-col justify-content-center outline-none`,
    size === LiliDropdownSize.Medium ? 'min-h-[80px]' : 'min-h-[40px]',
    disabled ? 'cursor-auto' : 'cursor-pointer'
  );

  useEffect(() => {
    if (shouldScrollOnOpen && isOptionPresented) {
      elDropDownWrapper.current?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [isOptionPresented]);

  return (
    <div ref={elDropDownWrapper} className={dropdownContainerStyle}>
      <Dropdown
        disabled={disabled}
        id={id}
        inputId={id}
        value={value?.value}
        onChange={(e) => {
          const newItem = items.find((item) => {
            if (typeof item.value === 'object' && optionValue) {
              return item.value?.[optionValue] === e.value?.[optionValue];
            }
            return item.value === e.value;
          });
          if (newItem) onSelect(newItem);
        }}
        pt={{
          input: { className: 'overflow-hidden min-w-0 flex-1' },
          trigger: {
            className: `${disabled ? 'cursor-auto' : 'cursor-pointer'} flex-shrink-0`,
          },
        }}
        loading={loading}
        options={items}
        optionLabel='optionLabel'
        className={dropdownStyle}
        dropdownIcon={
          <ArrowIcon
            className={`${
              disabled ? 'text-outline-primary-bw-05' : 'text-outline-primary-bw-06'
            } w-[24px] h-[24px] transition-transform`}
          />
        }
        collapseIcon={
          <ArrowIcon
            className={`${
              disabled ? 'text-outline-primary-bw-05' : 'text-outline-primary-bw-06'
            } w-[24px] h-[24px] rotate-180  transition-transform`}
          />
        }
        panelClassName={`shadow-lg bg-surface-primary-bw-01 border-[1px] border-outline-primary-bw-04 rounded-md overflow-scroll mt-[4px]  ${
          isOptionPresented ? 'max-h-[200px]' : 'max-h-[0px]'
        }`}
        placeholder={title}
        valueTemplate={() => (
          <LiliDropDownValueTemplate
            selectedValue={value?.value}
            selectedTitle={value?.title || ''}
            title={title}
            id={id}
            disabled={disabled}
            onClear={() => {
              if (onClear) {
                onClear();
              }
              setIsOptionPresented(false);
            }}
            showClear={showClear}
            Icon={Icon}
          />
        )}
        itemTemplate={(item: LiliDropdownItem) => (
          <LiliDropDownItemTemplate
            item={item}
            isSelected={isItemSelected(item)}
            optionValue={optionValue}
          />
        )}
        onBlur={onBlur}
        onFocus={() => {
          setIsOptionPresented(true);
          if (onFocus) onFocus();
        }}
        onHide={() => setIsOptionPresented(false)}
        unstyled
        tabIndex={tabIndex}
        {...otherProps}
      />
      <LiliError
        id={`${id}-error-container`}
        errorMessage={errorMessage}
        inlineChild={inlineChild}
        showIcon={size !== LiliDropdownSize.Small}
      />
    </div>
  );
};
